import { useEffect, useState, type CSSProperties } from 'react';
import { api } from './lib/api';
import { IconCheck } from './components/icons';

// IMG presence-check panel (additive). Renders ONLY when the CV's stream is 'img' (the server gates this via
// applies:false otherwise). OBSERVATIONAL + recover-framed + de-frameworked: it notes what IMG-stream programs
// COMMONLY look for and whether the CV already shows it — never an eligibility verdict. Pure display of the server's
// strings (no composition, no write). A present check is a quiet ✓; an absent/nudge check is a hedged suggestion.

interface ImgCheck { id: string; label: string; kind: 'presence' | 'nudge'; present: boolean | null; message: string }
interface ImgResp { applies: boolean; stream?: string | null; cycle?: string; disclaimer?: string; checks?: ImgCheck[] }

export function ImgPanel({ cvId }: { cvId: string }) {
  const [data, setData] = useState<ImgResp | null>(null);
  useEffect(() => { void (async () => {
    const r = await api<ImgResp>(`/api/coverage/cv/${cvId}/img`);
    if (r.ok) setData(r.data);
  })(); }, [cvId]);

  if (!data?.applies || !data.checks?.length) return null; // non-IMG streams render nothing

  // EVAL-SURFACE REDESIGN: the IMG status is a ONE-LINE STRIP on the overview, expanding on demand — it must not
  // compete with the CV-quality read for attention, and it stays explicitly SEPARATE from any quality judgment.
  // NUMERICS POLICY: the strip counts the checks NOT YET DETECTED (a cardinality naming an actionable list), never a
  // fraction of a total.
  const presence = data.checks.filter((ch) => ch.kind === 'presence');
  const notDetected = presence.filter((ch) => ch.present !== true).length;
  const strip = notDetected === 0
    ? 'IMG stream: everything these checks look for is visible on your CV.'
    : `IMG stream: ${notDetected} ${notDetected === 1 ? 'item is' : 'items are'} not detected on your CV. Not detected is not the same as a missing requirement.`;

  return (
    <section id="img-stream-checklist" style={S.main} data-testid="img-panel">
      <details style={S.panel}>
        <summary style={S.strip} data-testid="img-strip">{strip}</summary>
        <h2 style={S.h}>IMG eligibility-related completeness checks</h2>
        <p style={S.subhead}>
          Separate from your CV quality read — these check what is VISIBLE ON THE CV, not your eligibility. Nothing
          here is a judgment about the quality of your CV, and nothing here decides whether you are eligible.
        </p>
        {data.checks.map((ch) => (
          <p key={ch.id} style={ch.present === true ? S.present : S.suggest} data-testid={`img-check-${ch.id}`}>
            {ch.present === true ? <IconCheck aria-hidden="true" /> : ''}{checkText(ch)}
          </p>
        ))}
        {data.disclaimer && <p style={S.disclaimer} data-testid="img-disclaimer">{data.disclaimer}</p>}
      </details>
    </section>
  );
}

// C4 — client-only wording layer: prefixes each PRESENCE check with an explicit "Found on CV" / "Not found on CV" so
// the language never reads as an eligibility verdict, and hedges an absence with "cannot determine if it applies —
// program-dependent" UNLESS the server message already carries an equivalent hedge (avoids double-hedging). NUDGE
// checks (kind !== 'presence') are left as-is — they're a general reminder, not a found/not-found claim. The library
// message content itself (imgChecks.ts) is untouched; all wording composition happens here, client-side only.
function checkText(ch: ImgCheck): string {
  if (ch.kind !== 'presence') return ch.message; // nudge — unprefixed, as authored
  if (ch.present === true) {
    const trimmed = ch.message.replace(/^Your CV (shows|indicates)\s+/i, '');
    return `Found on CV — ${trimmed}`;
  }
  // EVAL-SURFACE REDESIGN (mechanical fix 4): an absence is stated as "NOT DETECTED ON YOUR CV" and is explicitly
  // distinguished, in the copy itself, from "a missing requirement" — the two were previously easy to conflate.
  const alreadyHedged = /program-dependent|some PRA pathways are exempt|if you also pursued/i.test(ch.message);
  const trimmed = ch.message.replace(/^Your CV doesn'?t (mention|show)\s+/i, '');
  const hedge = alreadyHedged ? '' : ' (Cannot determine if it applies — program-dependent.)';
  return `Not detected on your CV (this is not the same as a missing requirement) — ${trimmed}${hedge}`;
}

const S: Record<string, CSSProperties> = {
  main: { maxWidth: 'min(960px, 92vw)', margin: '8px auto 40px', padding: '0 var(--space-4)', fontFamily: 'var(--sans)', lineHeight: 'var(--lh-body)' },
  h: { fontSize: 'var(--fs-h3)', margin: 'var(--space-4) 0 var(--space-2)', color: 'var(--ink)' },
  subhead: { fontSize: 'var(--fs-small)', margin: '0 0 var(--space-3)', color: 'var(--muted)' },
  panel: { background: 'var(--surface-subtle)', border: '1px solid var(--rule)', borderRadius: 8, padding: 'var(--space-3) var(--space-4)', margin: 'var(--space-2) 0 var(--space-3)' },
  strip: { cursor: 'pointer', fontSize: 'var(--fs-body)', color: 'var(--ink)', lineHeight: 'var(--lh-body)' },
  present: { margin: '0 0 var(--space-2)', color: 'var(--ink-subtle)', fontSize: 'var(--fs-body)' },
  suggest: { margin: '0 0 var(--space-2)', color: 'var(--ink)', fontSize: 'var(--fs-body)' },
  disclaimer: { fontSize: 'var(--fs-label)', color: 'var(--muted)', fontStyle: 'italic', margin: '0' },
};
